import { FC, ReactNode, useState, useEffect, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import PercentageField from 'components/PercentageField'

interface PercentageRangeInputProps {
  defaultValue: number
  setDefaultValue: (value: number) => void
  minValue: number
  setMinValue: (value: number) => void
  maxValue: number
  setMaxValue: (value: number) => void
  label: string
  disabled?: boolean
  toggleAction?: ReactNode
}

const PercentageRangeInput: FC<PercentageRangeInputProps> = ({
  defaultValue,
  setDefaultValue,
  minValue,
  setMinValue,
  maxValue,
  setMaxValue,
  label,
  disabled = false,
  toggleAction,
}) => {
  const theme = useTheme()
  const [localDefaultValue, setLocalDefaultValue] = useState<string>(defaultValue.toString())
  const [localMinValue, setLocalMinValue] = useState<string>(minValue.toString())
  const [localMaxValue, setLocalMaxValue] = useState<string>(maxValue.toString())

  // Timeout refs for debouncing
  const minValueTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const defaultValueTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const maxValueTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Sync local state with prop changes
  useEffect(() => {
    const newLocalMinValue = minValue.toString()
    if (newLocalMinValue !== localMinValue) {
      setLocalMinValue(newLocalMinValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minValue])

  useEffect(() => {
    const newLocalDefaultValue = defaultValue.toString()
    if (newLocalDefaultValue !== localDefaultValue) {
      setLocalDefaultValue(newLocalDefaultValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]) // Only depend on defaultValue, not localDefaultValue

  useEffect(() => {
    const newLocalMaxValue = maxValue.toString()
    if (newLocalMaxValue !== localMaxValue) {
      setLocalMaxValue(newLocalMaxValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxValue])

  // Handle the value changes in the input fields
  const handleMinValueAdjustment = (newValue: string) => {
    setLocalMinValue(newValue)

    // Clear existing timeout
    if (minValueTimeoutRef.current) {
      clearTimeout(minValueTimeoutRef.current)
    }

    // Set new timeout for delayed state update
    minValueTimeoutRef.current = setTimeout(() => {
      const value = Number(newValue)
      setMinValue(value)

      // Validation and adjustment
      if (value > Number(localDefaultValue)) {
        setLocalDefaultValue(newValue)
        setDefaultValue(value)
      }
      if (value > Number(localMaxValue)) {
        setLocalMaxValue(newValue)
        setMaxValue(value)
      }
    }, 500)
  }

  const handleDefaultValueAdjustment = (newValue: string) => {
    setLocalDefaultValue(newValue)

    // Clear existing timeout
    if (defaultValueTimeoutRef.current) {
      clearTimeout(defaultValueTimeoutRef.current)
    }

    // Set new timeout for delayed state update
    defaultValueTimeoutRef.current = setTimeout(() => {
      const value = Number(newValue)
      setDefaultValue(value)

      // Validation and adjustment
      if (value < Number(localMinValue)) {
        setLocalMinValue(newValue)
        setMinValue(value)
      }
      if (value > Number(localMaxValue)) {
        setLocalMaxValue(newValue)
        setMaxValue(value)
      }
    }, 500)
  }

  const handleMaxValueAdjustment = (newValue: string) => {
    setLocalMaxValue(newValue)

    // Clear existing timeout
    if (maxValueTimeoutRef.current) {
      clearTimeout(maxValueTimeoutRef.current)
    }

    // Set new timeout for delayed state update
    maxValueTimeoutRef.current = setTimeout(() => {
      const value = Number(newValue)
      setMaxValue(value)

      // Validation and adjustment
      if (value < Number(localDefaultValue)) {
        setLocalDefaultValue(newValue)
        setDefaultValue(value)
      }
      if (value < Number(localMinValue)) {
        setLocalMinValue(newValue)
        setMinValue(value)
      }
    }, 500)
  }

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (minValueTimeoutRef.current) {
        clearTimeout(minValueTimeoutRef.current)
      }
      if (defaultValueTimeoutRef.current) {
        clearTimeout(defaultValueTimeoutRef.current)
      }
      if (maxValueTimeoutRef.current) {
        clearTimeout(maxValueTimeoutRef.current)
      }
    }
  }, [])

  return (
    <Box sx={{ minWidth: '200px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '10px' }}>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.primary.main,
            fontSize: '0.75rem',
            lineHeight: '1rem',
          }}
        >
          {label}
        </Typography>
        {toggleAction}
      </Box>
      <Box display="flex" gap={2}>
        <PercentageField
          label="Minimum"
          value={localMinValue}
          onChange={handleMinValueAdjustment}
          name="Testing"
          decimalPlaces={0}
          shrink
          variant="outlined"
          pill
          disabled={disabled}
          sx={{ width: '90px', minWidth: '90px', maxWidth: '90px' }}
        />
        <PercentageField
          label="Default"
          value={localDefaultValue}
          onChange={handleDefaultValueAdjustment}
          name="Testing"
          decimalPlaces={0}
          shrink
          variant="outlined"
          pill
          disabled={disabled}
          sx={{ width: '90px', minWidth: '90px', maxWidth: '90px' }}
        />
        <PercentageField
          label="Maximum"
          value={localMaxValue}
          onChange={handleMaxValueAdjustment}
          name="Testing"
          decimalPlaces={0}
          shrink
          variant="outlined"
          pill
          disabled={disabled}
          sx={{ width: '90px', minWidth: '90px', maxWidth: '90px' }}
        />
      </Box>
    </Box>
  )
}

export default PercentageRangeInput
